/**
 * RuleComplianceStatCards Component
 *
 * Displays summary stat cards for rule compliance metrics
 */

import React from 'react';
import {
  Assessment as RulesIcon,
  CheckCircle as CompliantIcon,
  Error as ViolatedIcon,
  TrendingUp as ComplianceIcon,
} from '@mui/icons-material';
import { Box, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import type { RuleComplianceStatsResponse } from '@store/generated/jotaudit';

interface RuleComplianceStatCardsProps {
  /** Most violated rule */
  mostViolatedRule: RuleComplianceStatsResponse | null;
  /** Most compliant rule */
  mostCompliantRule: RuleComplianceStatsResponse | null;
  /** Total number of rules */
  totalRules: number;
  /** Overall compliance rate */
  overallComplianceRate: number | null;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Whether there's a leftmost card (adjusts grid sizing) */
  hasLeftmostCard?: boolean;
  /** Callback when clicking a rule card */
  onRuleClick?: (ruleId: string, ruleName?: string, complianceRate?: number, violationCount?: number) => void;
  /** Organization ID for admin mode */
  organizationId?: string;
}

/**
 * Format percentage
 */
const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
};

interface CompactStatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number | null | undefined;
  subtitle?: string;
  isLoading?: boolean;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  /** Whether the card is clickable */
  onClick?: () => void;
  /** Whether the rule is deleted */
  isDeleted?: boolean;
  /** Whether the rule is disabled */
  isDisabled?: boolean;
}

const CompactStatCard: React.FC<CompactStatCardProps> = ({
  icon,
  label,
  value,
  subtitle,
  isLoading = false,
  color = 'primary',
  onClick,
  isDeleted = false,
  isDisabled = false,
}) => {
  const theme = useTheme();

  const colorMap = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const bgColorMap = {
    primary: theme.palette.primary.light + '20',
    success: theme.palette.success.light + '20',
    warning: theme.palette.warning.light + '20',
    error: theme.palette.error.light + '20',
    info: theme.palette.info.light + '20',
  };

  const displayValue = value === null || value === undefined ? '—' : value;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: onClick ? 'all 0.2s ease' : 'none',
        opacity: isDisabled ? 0.6 : 1,
        '&:hover': onClick
          ? {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
            }
          : {},
      }}
    >
      {/* Icon and Label Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: bgColorMap[color],
            color: colorMap[color],
            flexShrink: 0,
            '& svg': {
              fontSize: 18,
            },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            fontSize: '0.75rem',
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Value */}
      {isLoading ? (
        <Skeleton sx={{width: "80%", height: 24,  mb: 0.5 }} />
      ) : (
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: isDisabled
              ? theme.palette.text.disabled
              : isDeleted
                ? theme.palette.text.secondary
                : theme.palette.text.primary,
            lineHeight: 1.4,
            mb: subtitle ? 0.5 : 0,
            fontSize: typeof displayValue === 'string' && displayValue.length > 30 ? '0.875rem' : '1rem',
            textDecoration: isDeleted ? 'line-through' : 'none',
          }}
        >
          {displayValue}
        </Typography>
      )}

      {/* Subtitle */}
      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.7rem',
            mt: 'auto',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};

export const RuleComplianceStatCards: React.FC<RuleComplianceStatCardsProps> = ({
  mostViolatedRule,
  mostCompliantRule,
  totalRules,
  overallComplianceRate,
  isLoading = false,
  hasLeftmostCard = false,
  onRuleClick,
  organizationId,
}) => {
  const { jotauditDashboard } = useEventTracking();
  
  // When there's a leftmost card, we have 9 columns left (12 - 3), so each card gets ~2.25
  // Use md={2} for 4 cards = 8 columns, leaving 1 column gap, or use md={2.4} equivalent
  // Actually, let's use md={2} and accept the spacing, or use a flex approach
  // Best: use md={2} for each when hasLeftmostCard, which gives us 8 columns used
  const mdSize = hasLeftmostCard ? 2 : 3;

  // Handle most violated rule click
  const handleMostViolatedClick = React.useCallback(() => {
    if (mostViolatedRule?.rule_id) {
      // Track stat card click
      jotauditDashboard.statCardClicked({
        card_type: 'most_violated',
        rule_id: mostViolatedRule.rule_id,
      });
      
      if (onRuleClick) {
        onRuleClick(
          mostViolatedRule.rule_id,
          mostViolatedRule.rule_name || mostViolatedRule.rule_id,
          mostViolatedRule.compliance_rate,
          mostViolatedRule.non_compliant_count || 0
        );
      }
    }
  }, [mostViolatedRule, onRuleClick, jotauditDashboard]);

  // Handle most compliant rule click
  const handleMostCompliantClick = React.useCallback(() => {
    if (mostCompliantRule?.rule_id) {
      // Track stat card click
      jotauditDashboard.statCardClicked({
        card_type: 'most_compliant',
        rule_id: mostCompliantRule.rule_id,
      });
      
      if (onRuleClick) {
        onRuleClick(
          mostCompliantRule.rule_id,
          mostCompliantRule.rule_name || mostCompliantRule.rule_id,
          mostCompliantRule.compliance_rate,
          mostCompliantRule.non_compliant_count || 0
        );
      }
    }
  }, [mostCompliantRule, onRuleClick, jotauditDashboard]);

  return (
    <>
      {/* Most Violated Rule */}
      <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
        <CompactStatCard
          icon={<ViolatedIcon />}
          label="Most Violated Rule"
          value={
            mostViolatedRule?.rule_name || mostViolatedRule?.rule_id || '—'
          }
          subtitle={
            mostViolatedRule?.violation_rate !== undefined
              ? `${formatPercent(mostViolatedRule.violation_rate)} violation rate`
              : undefined
          }
          isLoading={isLoading}
          color="error"
          onClick={mostViolatedRule?.rule_id ? handleMostViolatedClick : undefined}
          isDeleted={mostViolatedRule?.is_deleted}
          isDisabled={mostViolatedRule?.is_disabled}
        />
      </Grid>

      {/* Most Compliant Rule */}
      <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
        <CompactStatCard
          icon={<CompliantIcon />}
          label="Most Compliant Rule"
          value={
            mostCompliantRule?.rule_name || mostCompliantRule?.rule_id || '—'
          }
          subtitle={
            mostCompliantRule?.compliance_rate !== undefined
              ? `${formatPercent(mostCompliantRule.compliance_rate)} compliance rate`
              : undefined
          }
          isLoading={isLoading}
          color="success"
          onClick={mostCompliantRule?.rule_id ? handleMostCompliantClick : undefined}
          isDeleted={mostCompliantRule?.is_deleted}
          isDisabled={mostCompliantRule?.is_disabled}
        />
      </Grid>

      {/* Total Rules Evaluated */}
      <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
        <CompactStatCard
          icon={<RulesIcon />}
          label="Total Rules Evaluated"
          value={totalRules}
          subtitle="Rules with audit data"
          isLoading={isLoading}
          color="primary"
        />
      </Grid>

      {/* Overall Compliance Rate */}
      <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
        <CompactStatCard
          icon={<ComplianceIcon />}
          label="Overall Compliance Rate"
          value={formatPercent(overallComplianceRate)}
          subtitle="Average across all rules"
          isLoading={isLoading}
          color="info"
        />
      </Grid>
    </>
  );
};

export default RuleComplianceStatCards;

